/**
 * v1.4.26 — App Store Readiness Checklist.
 *
 * Static per-store checklist that helps the admin see at a glance
 * which submission artefacts have been generated (AAB, PDF, screen-
 * shots, scaffolds) and which are still outstanding. Each row links
 * to a downloadable artefact when one is available on the backend
 * `/api/downloads` mount, or shows an "action needed" hint when the
 * item is a manual step (e.g. "Fill in the Play Store form").
 *
 * The checklist is intentionally NOT wired to a database — this
 * keeps it a light-weight, read-only aid. The source of truth for
 * "shipped or not" is whether the file at `/api/downloads/<name>`
 * returns HTTP 200.
 */
import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2,
  Circle,
  Download,
  ExternalLink,
  Loader2,
  Store,
  Smartphone,
  ShieldCheck,
  ShieldAlert,
  ShieldQuestion,
  HardDrive,
  Wrench,
} from "lucide-react";
import { useHealthStatus } from "@/admin/lib/useHealthStatus";
import { kickStorageMaintenance } from "@/admin/lib/api";
import { useToast } from "@/hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Each checklist section (Play, App Store, Smart TV) is a list of
// `{ id, label, description, artefact?, external? }` items. `artefact`
// resolves to `/api/downloads/<artefact>` and lights the row green
// when the file exists; `external` opens a docs link in a new tab.
const SECTIONS = [
  {
    id: "distribution",
    title: "Direct sideload / distribution",
    icon: Smartphone,
    color: "violet",
    items: [
      {
        id: "apk-current",
        label: "Latest signed APK (direct sideload)",
        description: "Currently-shipping APK for direct download / Dropbox OTA.",
        artefact: "ZA-Player-Pro-Web-1.4.70.apk",
      },
      {
        id: "aab-current",
        label: "Latest signed AAB (archive)",
        description: "Currently-shipping release bundle.",
        artefact: "ZA-Player-Pro-Web-1.4.70-release.aab",
      },
      {
        id: "server-update",
        label: "zaplayerpro.club admin update",
        description: "tar.gz to push to production via update.sh.",
        artefact: "zaplayerpro-admin-update.tar.gz",
      },
    ],
  },
];

async function probeArtefact(name) {
  if (!BACKEND_URL) return { ok: false, size: 0 };
  try {
    // HEAD is enough — we just want existence + size.
    const r = await fetch(`${BACKEND_URL}/api/downloads/${name}`, {
      method: "HEAD",
      cache: "no-store",
    });
    if (!r.ok) return { ok: false, size: 0 };
    const size = parseInt(r.headers.get("content-length") || "0", 10);
    return { ok: true, size };
  } catch {
    return { ok: false, size: 0 };
  }
}

function fmtSize(bytes) {
  if (!bytes) return "";
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

// v1.4.28 — Chip that surfaces the last `nightly-smoketest` daemon
// run in one glance. Colors follow the standard traffic-light
// convention:
//   * pass    → green  (last run passed)
//   * fail    → red    (assertions failed, click through to logs)
//   * stale   → amber  (daemon hasn't checked in for > 36 h)
//   * unknown → slate  (daemon just booted / no report yet)
//   * error   → slate  (network / auth error fetching the endpoint)
function fmtAge(hours) {
  if (hours == null) return "";
  if (hours < 1) return `${Math.round(hours * 60)} min ago`;
  if (hours < 48) return `${hours.toFixed(1)} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

function SmokeHealthChip({ smoke, error }) {
  if (error) {
    return (
      <div
        className="mt-3 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-500"
        data-testid="smoke-health-chip"
        data-state="error"
      >
        <ShieldQuestion className="w-3.5 h-3.5" />
        Nightly regression — couldn&apos;t reach /api/admin/health/smoke
      </div>
    );
  }
  if (!smoke) {
    return (
      <div
        className="mt-3 inline-flex items-center gap-2 text-xs text-slate-500"
        data-testid="smoke-health-chip"
        data-state="loading"
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Loading nightly regression status…
      </div>
    );
  }
  const state = smoke.state || "unknown";
  const style = {
    pass:    { chip: "border-emerald-200 bg-emerald-50 text-emerald-700", icon: <ShieldCheck className="w-3.5 h-3.5" />, label: "Nightly passing" },
    fail:    { chip: "border-red-200 bg-red-50 text-red-700",             icon: <ShieldAlert className="w-3.5 h-3.5" />, label: "Nightly failing" },
    stale:   { chip: "border-amber-200 bg-amber-50 text-amber-700",       icon: <ShieldAlert className="w-3.5 h-3.5" />, label: "Nightly daemon idle" },
    unknown: { chip: "border-slate-200 bg-slate-50 text-slate-500",       icon: <ShieldQuestion className="w-3.5 h-3.5" />, label: "Nightly not yet run" },
  }[state] || { chip: "border-slate-200 bg-slate-50 text-slate-500", icon: <ShieldQuestion className="w-3.5 h-3.5" />, label: "Nightly status unknown" };

  const detail = smoke.summary
    ? `${smoke.summary} · ${fmtAge(smoke.run_age_h)}`
    : fmtAge(smoke.run_age_h) || "no run recorded";

  return (
    <div
      className={`mt-3 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${style.chip}`}
      data-testid="smoke-health-chip"
      data-state={state}
      title={
        `Latest nightly-smoketest run:\n` +
        `state: ${state}\n` +
        `run_at: ${smoke.run_at || "—"}\n` +
        `duration: ${smoke.duration_s ?? "—"}s\n` +
        `suites:\n  ${(smoke.suites || []).join("\n  ")}`
      }
    >
      {style.icon}
      <span className="font-medium">{style.label}</span>
      <span className="text-[11px] opacity-80" data-testid="smoke-health-detail">
        {detail}
      </span>
    </div>
  );
}

// v1.4.28-f — sibling chip for the `git-maintenance` supervisor
// daemon. Same 3-state colour scheme mapped to filesystem headroom:
//   * pass     → green (≤ 85 % used)
//   * warn     → amber (85 – 95 %)
//   * critical → red   (> 95 %)
// Detail line surfaces "X MB free · loose objects Y · last freed Z MB"
// so the admin can see both the current pressure AND whether the
// daemon actually kicked in when they'd expect it to.
function fmtMb(mb) {
  if (mb == null) return "—";
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb} MB`;
}

function StorageHealthChip({ storage, error }) {
  if (error) {
    return (
      <div
        className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-500"
        data-testid="storage-health-chip"
        data-state="error"
      >
        <ShieldQuestion className="w-3.5 h-3.5" />
        Storage — couldn&apos;t reach /api/admin/health/storage
      </div>
    );
  }
  if (!storage) {
    return (
      <div
        className="inline-flex items-center gap-2 text-xs text-slate-500"
        data-testid="storage-health-chip"
        data-state="loading"
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Loading storage health…
      </div>
    );
  }
  const state = storage.state || "pass";
  const style = {
    pass:     { chip: "border-emerald-200 bg-emerald-50 text-emerald-700", label: "Storage healthy" },
    warn:     { chip: "border-amber-200 bg-amber-50 text-amber-700",       label: "Storage low" },
    critical: { chip: "border-red-200 bg-red-50 text-red-700",             label: "Storage critical" },
  }[state] || { chip: "border-slate-200 bg-slate-50 text-slate-500", label: "Storage unknown" };

  const maint = storage.maintenance || {};
  const maintPart = maint.last_run_at
    ? `daemon ${fmtAge(maint.run_age_h)}${
        maint.last_freed_mb != null ? ` (freed ${maint.last_freed_mb} MB)` : ""
      }`
    : "daemon never ran";
  const detail = `${storage.disk_pct}% used · ${fmtMb(storage.disk_free_mb)} free · ${maintPart}`;

  const title =
    `Storage health\n` +
    `disk: ${storage.disk_used_mb} / ${storage.disk_total_mb} MB (${storage.disk_pct}%)\n` +
    `.git/objects: ${fmtMb(storage.git_objects_mb)} across ${storage.git_loose_objects} loose objects\n` +
    `webpack cache: ${fmtMb(storage.webpack_cache_mb)}\n` +
    `last git-maintenance run: ${maint.last_run_at || "—"}` +
    (maint.last_freed_mb != null ? `\nlast freed: ${maint.last_freed_mb} MB` : "");

  return (
    <div
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${style.chip}`}
      data-testid="storage-health-chip"
      data-state={state}
      title={title}
    >
      <HardDrive className="w-3.5 h-3.5" />
      <span className="font-medium">{style.label}</span>
      <span className="text-[11px] opacity-80" data-testid="storage-health-detail">
        {detail}
      </span>
    </div>
  );
}

// v1.4.28-i — "Kick maintenance now" button. Sits next to the
// storage chip and POSTs to `/api/admin/health/storage/maintenance-run`,
// which shells out to `git-maintenance.sh --once` server-side.
// Toasts the freed-MB delta on success so the admin gets immediate
// feedback without waiting for the storage chip's next 60 s poll.
function KickMaintenanceButton() {
  const [running, setRunning] = useState(false);
  const { toast } = useToast();

  const kick = async () => {
    if (running) return;
    setRunning(true);
    try {
      const data = await kickStorageMaintenance();
      const ok = data.status === "ok";
      const freed = data.freed_mb ?? 0;
      const after = data.free_after_mb;
      toast({
        title: ok ? "Maintenance done" : `Maintenance ${data.status}`,
        description: ok
          ? `Freed ${freed} MB in ${data.duration_s}s. ${after} MB now free.`
          : `Exit code ${data.exit_code}. Check /var/log/supervisor/git-maintenance.out.log`,
        variant: ok ? "default" : "destructive",
      });
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        status === 409
          ? "Another maintenance run is already in progress."
          : err?.response?.data?.detail || err?.message || "Unknown error";
      toast({
        title: "Maintenance failed",
        description: msg,
        variant: "destructive",
      });
    } finally {
      setRunning(false);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={kick}
      disabled={running}
      className="h-7 px-3 text-xs gap-1.5 border-slate-300"
      data-testid="kick-maintenance-btn"
      data-running={running ? "true" : "false"}
      title="Run one git-maintenance pass on the server — prunes .git tmp files and clears the webpack dev-cache."
    >
      {running ? (
        <>
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Running…
        </>
      ) : (
        <>
          <Wrench className="w-3.5 h-3.5" />
          Kick maintenance
        </>
      )}
    </Button>
  );
}


export default function AppStoreReadiness() {
  const [statuses, setStatuses] = useState({}); // { artefact: { ok, size } }
  const [loading, setLoading] = useState(true);
  // v1.4.28-h — polling now centralised in `useHealthStatus`.
  const { smoke, smokeError, storage, storageError } = useHealthStatus();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const wanted = SECTIONS.flatMap((s) =>
        s.items.filter((i) => i.artefact).map((i) => i.artefact),
      );
      const unique = Array.from(new Set(wanted));
      const results = await Promise.all(unique.map((n) => probeArtefact(n)));
      if (cancelled) return;
      const next = {};
      unique.forEach((n, i) => (next[n] = results[i]));
      setStatuses(next);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const totalReady = Object.values(statuses).filter((s) => s.ok).length;
  const totalTracked = Object.keys(statuses).length;

  return (
    <div className="space-y-6" data-testid="admin-app-store-readiness">
      <div>
        <h1 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
          <Store className="w-5 h-5 text-emerald-500" />
          App Store Readiness
        </h1>
        <p className="text-sm text-slate-600 mt-1 max-w-3xl">
          Quick checklist that surfaces which submission artefacts are already
          built and downloadable from this admin panel. Green rows are ready
          to grab; grey rows still need a build step. Manual steps (
          <em>e.g. filling in the Play Console form</em>) are grouped at the
          end of each section.
        </p>
        {loading ? (
          <div className="mt-3 text-xs text-slate-500 inline-flex items-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Probing downloads mount…
          </div>
        ) : (
          <div
            className="mt-3 text-xs text-slate-500"
            data-testid="admin-app-store-readiness-summary"
          >
            <strong className="text-slate-700">{totalReady}</strong> of{" "}
            <strong className="text-slate-700">{totalTracked}</strong> artefacts
            are on the server.
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-2 items-center">
          <SmokeHealthChip smoke={smoke} error={smokeError} />
          <StorageHealthChip storage={storage} error={storageError} />
          <KickMaintenanceButton />
        </div>
      </div>

      {SECTIONS.map((section) => (
        <Card
          key={section.id}
          data-testid={`readiness-section-${section.id}`}
        >
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-slate-800">
              <section.icon
                className={`w-4 h-4 text-${section.color}-500`}
              />
              {section.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {section.items.map((item) => {
              const st = item.artefact ? statuses[item.artefact] : null;
              const ready = item.artefact ? !!st?.ok : false;
              const isExternal = !item.artefact && !!item.external;
              return (
                <div
                  key={item.id}
                  data-testid={`readiness-item-${section.id}-${item.id}`}
                  className={`flex items-start gap-3 p-3 rounded-md border transition-colors ${
                    ready
                      ? "bg-emerald-50/50 border-emerald-200"
                      : "bg-slate-50/50 border-slate-200"
                  }`}
                >
                  <div className="shrink-0 pt-0.5">
                    {ready ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <Circle className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800">
                      {item.label}
                      {ready && st?.size ? (
                        <span className="ml-2 text-[10px] font-normal text-emerald-700">
                          {fmtSize(st.size)}
                        </span>
                      ) : null}
                    </div>
                    <div className="text-xs text-slate-500 leading-snug mt-0.5">
                      {item.description}
                    </div>
                    {item.artefact ? (
                      <div className="text-[10px] text-slate-400 mt-1 font-mono truncate">
                        {item.artefact}
                      </div>
                    ) : null}
                  </div>
                  <div className="shrink-0">
                    {item.artefact && ready ? (
                      <Button
                        asChild
                        size="sm"
                        variant="outline"
                        data-testid={`readiness-download-${section.id}-${item.id}`}
                        className="text-xs"
                      >
                        <a
                          href={`${BACKEND_URL}/api/downloads/${item.artefact}`}
                          download
                        >
                          <Download className="w-3.5 h-3.5 mr-1" />
                          Download
                        </a>
                      </Button>
                    ) : isExternal ? (
                      <Button
                        asChild
                        size="sm"
                        variant="outline"
                        data-testid={`readiness-external-${section.id}-${item.id}`}
                        className="text-xs"
                      >
                        <a
                          href={item.external}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ExternalLink className="w-3.5 h-3.5 mr-1" />
                          Open
                        </a>
                      </Button>
                    ) : item.artefact ? (
                      <span className="text-[10px] text-slate-400">not built yet</span>
                    ) : (
                      <span className="text-[10px] text-slate-400">manual</span>
                    )}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
